import {t} from 'i18next';

export interface Region {
  id: string;
  label: string;
  timeZone: string;
  workStartHour: number;
  workEndHour: number;
}

export interface LocalTimeParts {
  hour: number;
  minute: number;
  label: string;
}

export function localTime(now: number, timeZone: string): LocalTimeParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date(now));
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  const label = formatter.format(new Date(now));
  return {hour, minute, label};
}

export function isWithinWorkingHours(now: number, region: Region): boolean {
  const {hour, minute} = localTime(now, region.timeZone);
  const fractional = hour + minute / 60;
  return fractional >= region.workStartHour && fractional < region.workEndHour;
}

/** US East + West overlap window in Pacific local hours (demo). */
export const CORE_HOURS_PT = {start: 8, end: 11};

export function isWithinCoreHoursPt(now: number): boolean {
  const pt = localTime(now, 'America/Los_Angeles');
  const fractional = pt.hour + pt.minute / 60;
  return fractional >= CORE_HOURS_PT.start && fractional < CORE_HOURS_PT.end;
}

export function coreHoursSummary(now: number): {
  label: string;
  active: boolean;
  startsInMinutes: number | null;
} {
  const active = isWithinCoreHoursPt(now);
  if (active) {
    return {
      label: `Core hours ${CORE_HOURS_PT.start}:00–${CORE_HOURS_PT.end}:00 PT · live now`,
      active: true,
      startsInMinutes: null,
    };
  }

  const pt = localTime(now, 'America/Los_Angeles');
  const fractional = pt.hour + pt.minute / 60;
  let minutesUntil: number;
  if (fractional < CORE_HOURS_PT.start) {
    minutesUntil = Math.round((CORE_HOURS_PT.start - fractional) * 60);
  } else {
    minutesUntil = Math.round((24 - fractional + CORE_HOURS_PT.start) * 60);
  }

  const hours = Math.floor(minutesUntil / 60);
  const mins = minutesUntil % 60;
  const startsLabel = hours > 0 ? `${hours}h ${mins > 0 ? `${mins}m` : ''}`.trim() : `${mins}m`;

  return {
    label: `Core window starts in ${startsLabel}`,
    active: false,
    startsInMinutes: minutesUntil,
  };
}

/** The viewer's own IANA timezone, used as the reference point for offsets. */
export function getViewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Hours east of UTC for `timeZone` at `now`, rounded to the nearest half hour. */
export function zoneOffsetHours(now: number, timeZone: string): number {
  const date = new Date(now);
  const utc = new Date(date.toLocaleString('en-US', {timeZone: 'UTC'}));
  const local = new Date(date.toLocaleString('en-US', {timeZone}));
  return Math.round(((local.getTime() - utc.getTime()) / 3600000) * 2) / 2;
}

// Zones that keep their own row even when a neighbour shares the offset, so
// Ireland is never shown under the UK's name.
const STANDALONE_ZONES = new Set(['Europe/Dublin']);

/**
 * Row key for grouping teammates: the current UTC offset, plus the zone itself
 * for zones in STANDALONE_ZONES.
 */
export function zoneBucketKey(now: number, timeZone: string): string {
  const offset = zoneOffsetHours(now, timeZone);
  return STANDALONE_ZONES.has(timeZone) ? `${offset}:${timeZone}` : `${offset}`;
}

/** Signed hour delta of `timeZone` relative to the viewer's zone, e.g. +3 / -9. */
export function offsetFromViewer(now: number, timeZone: string, viewerTimeZone: string): number {
  return zoneOffsetHours(now, timeZone) - zoneOffsetHours(now, viewerTimeZone);
}

export function formatOffsetLabel(deltaHours: number): string {
  if (deltaHours === 0) {
    return 'same time';
  }
  const sign = deltaHours > 0 ? '+' : '-';
  const abs = Math.abs(deltaHours);
  const value = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1);
  return `${sign}${value}h`;
}

/**
 * Human phrasing of an offset relative to the viewer. Mirrors the copy used on
 * the profile pages (`calculateTimeDifference`): "Same as yours" /
 * "N hour(s) ahead" / "N hour(s) behind".
 */
export function formatOffsetRelative(deltaHours: number): string {
  if (deltaHours === 0) {
    return t('dashboard_tz_same_as_yours').toString();
  }
  const abs = Math.abs(deltaHours);
  const isOne = abs === 1;
  if (deltaHours > 0) {
    return t(isOne ? 'dashboard_tz_hour_ahead' : 'dashboard_tz_hours_ahead', {
      count: abs,
    }).toString();
  }
  return t(isOne ? 'dashboard_tz_hour_behind' : 'dashboard_tz_hours_behind', {
    count: abs,
  }).toString();
}

/** Short timezone abbreviation for `timeZone` at `now`, e.g. "GMT+2", "PDT". */
export function zoneAbbrev(now: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short',
  }).formatToParts(new Date(now));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
}

/**
 * Friendly, daylight-agnostic timezone name, e.g. "Eastern Time",
 * "Pacific Time", "Central European Time". Used to label a zone that spans
 * multiple cities, where naming a single city would be misleading.
 */
export function zoneFriendlyName(now: number, timeZone: string): string {
  // Intl's generic name is "Ireland Time"; the EMEA office calls it this.
  if (timeZone === 'Europe/Dublin') {
    return 'Irish Standard Time';
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longGeneric',
  }).formatToParts(new Date(now));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
}

/**
 * Local clock label for `timeZone`, formatted in the viewer's own locale so
 * US colleagues see 12h (e.g. "8:04 AM") while 24h locales see "08:04".
 */
export function formatZoneTime(now: number, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(now));
}

/** Default working-day window (local hours) used when a region isn't supplied. */
export const DEFAULT_WORK_START_HOUR = 7;
export const DEFAULT_WORK_END_HOUR = 19;

/**
 * True when the local time in `timeZone` falls outside the working day, i.e.
 * before `startHour` or at/after `endHour`. Used to show 🌙 after the zone
 * label when teammates are likely off the clock (early morning / evening).
 */
export function isOutsideWorkingHours(
  now: number,
  timeZone: string,
  startHour: number = DEFAULT_WORK_START_HOUR,
  endHour: number = DEFAULT_WORK_END_HOUR
): boolean {
  const {hour, minute} = localTime(now, timeZone);
  const fractional = hour + minute / 60;
  return fractional < startHour || fractional >= endHour;
}

export type RegionStatus = 'working' | 'core' | 'off';

export function regionStatus(now: number, region: Region): RegionStatus {
  if (isWithinWorkingHours(now, region)) {
    if (region.id === 'us-east' || region.id === 'us-west') {
      return isWithinCoreHoursPt(now) ? 'core' : 'working';
    }
    return 'working';
  }
  return 'off';
}
